"use client";

import { motion } from "motion/react";

type LogoGroup = {
  name: string;
  logos: { src: string; alt: string }[];
};

const INTEGRATION_GROUPS: LogoGroup[] = [
  {
    name: "ERP Systems",
    logos: [
      {
        src: "/assets/svg/Integrations/ERP/sap_logo_white.svg",
        alt: "SAP Logo",
      },
      {
        src: "/assets/svg/Integrations/ERP/workday_logo_white.svg",
        alt: "Workday Logo",
      },
      {
        src: "/assets/svg/Integrations/ERP/oracle_logo_white.svg",
        alt: "Oracle Logo",
      },
      {
        src: "/assets/svg/Integrations/ERP/infor_logo_white.svg",
        alt: "Infor Logo",
      },
      {
        src: "/assets/svg/Integrations/ERP/ibm_logo_white.svg",
        alt: "IBM Logo",
      },
    ],
  },
  {
    name: "AI Model Providers",
    logos: [
      {
        src: "/assets/svg/Integrations/ModelProviders/anthropic_logo_white.svg",
        alt: "Anthropic Logo",
      },
      {
        src: "/assets/svg/Integrations/ModelProviders/openai_logo_white.svg",
        alt: "OpenAI Logo",
      },
      {
        src: "/assets/svg/Integrations/ModelProviders/google_gemini_logo_white.svg",
        alt: "Google Gemini Logo",
      },
      {
        src: "/assets/svg/Integrations/ModelProviders/azureai.png",
        alt: "Azure AI Logo",
      },
      {
        src: "/assets/svg/Integrations/ModelProviders/aws_bedrock_logo_white.svg",
        alt: "Amazon Bedrock Logo",
      },
      {
        src: "/assets/svg/Integrations/ModelProviders/XAI_Logo.svg",
        alt: "xAI Grok Logo",
      },
    ],
  },
  {
    name: "Data Warehouses & Lakes",
    logos: [
      {
        src: "/assets/svg/Integrations/DataLake_Warehouses/snowflake_logo_white.svg",
        alt: "Snowflake Logo",
      },
      {
        src: "/assets/svg/Integrations/DataLake_Warehouses/databricks_logo_white.svg",
        alt: "Databricks Logo",
      },
      {
        src: "/assets/svg/Integrations/DataLake_Warehouses/amazon_redshift_logo_white.svg",
        alt: "Amazon Redshift Logo",
      },
      {
        src: "/assets/svg/Integrations/DataLake_Warehouses/google_big_query_logo_white.svg",
        alt: "Google BigQuery Logo",
      },
      {
        src: "/assets/svg/Integrations/DataLake_Warehouses/postgresql_logo_white.svg",
        alt: "PostgreSQL Logo",
      },
    ],
  },
  {
    name: "Document Repositories",
    logos: [
      {
        src: "/assets/svg/Integrations/DocRepos/sharepoint_logo_white.svg",
        alt: "SharePoint Logo",
      },
      {
        src: "/assets/svg/Integrations/DocRepos/box_logo_white.svg",
        alt: "Box Logo",
      },
      {
        src: "/assets/svg/Integrations/DocRepos/dropbox_logo_white.svg",
        alt: "Dropbox Logo",
      },
      {
        src: "/assets/svg/Integrations/DocRepos/google_cloud_logo_white.svg",
        alt: "Google Cloud Logo",
      },
    ],
  },
];

export function IntegrationsSection() {
  return (
    <section className="relative flex h-full w-full items-center overflow-hidden bg-black py-10 lg:py-14">
      <div className="relative mx-auto w-full max-w-6xl px-5 lg:px-8">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-10 text-center"
        >
          <h2 className="text-3xl font-sans font-semibold tracking-tight text-white lg:text-4xl">
            Connect with your systems
          </h2>
        </motion.div>

        {/* Integration Categories */}
        <div className="grid gap-6 lg:grid-cols-2">
          {INTEGRATION_GROUPS.map((group, index) => (
            <IntegrationCategory
              key={group.name}
              title={group.name}
              logos={group.logos}
              delay={index * 0.12}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function IntegrationCategory({
  title,
  logos,
  delay = 0
}: {
  title: string;
  logos: { src: string; alt: string }[];
  delay?: number;
}) {
  const icon = title.includes("ERP")
    ? "briefcase"
    : title.includes("Model")
      ? "sparkles"
      : title.includes("Warehouses")
        ? "database"
        : "folder";

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay }}
      className="relative overflow-hidden rounded-2xl bg-white/[0.03] p-5 sm:p-6"
    >
      <div className="relative mb-6 flex items-center gap-3 text-white">
        <div className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/10 text-white/80">
          <SectionIcon kind={icon} />
        </div>
        <h3 className="text-lg font-semibold tracking-tight text-white">
          {title}
        </h3>
      </div>
      <div className="relative flex flex-wrap items-center justify-center gap-x-6 gap-y-4">
        {logos.map((logo, index) => (
          <motion.div
            key={logo.src}
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: delay + 0.1 * index }}
            className="flex-shrink-0"
          >
            <LogoTile logo={logo} />
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

function LogoTile({ logo }: { logo: { src: string; alt: string } }) {
  return (
    <div className="group flex h-12 min-w-[110px] items-center justify-center rounded-lg bg-transparent px-2 transition-all hover:-translate-y-1">
      <img
        src={logo.src}
        alt={logo.alt}
        className="h-6 max-w-[110px] object-contain opacity-90 transition-opacity group-hover:opacity-100"
        loading="lazy"
      />
    </div>
  );
}

function SectionIcon({ kind }: { kind: "briefcase" | "sparkles" | "database" | "folder" | string }) {
  switch (kind) {
    case "briefcase":
      return (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2m4 0v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7h18z" />
        </svg>
      );
    case "sparkles":
      return (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 4l2 4 4 2-4 2-2 4-2-4-4-2 4-2 2-4z" transform="translate(8 2)" />
        </svg>
      );
    case "database":
      return (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <ellipse cx="12" cy="5" rx="7" ry="3" />
          <path d="M5 5v14c0 1.657 3.134 3 7 3s7-1.343 7-3V5" />
        </svg>
      );
    default:
      return (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7a2 2 0 012-2h5l2 2h7a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
        </svg>
      );
  }
}
